/**
 * Utilidades de formato numérico según la cultura del tenant.
 * Brasil (pt-BR): millares con punto y decimales con coma (ej. 5.555,20).
 */
export function getCurrentCulture() {
    const cultureElement = document.getElementById('currentCulture');
    if (cultureElement?.value?.trim()) {
        return cultureElement.value.trim();
    }
    if (typeof window !== 'undefined' && window.kendoLocaleManager?.getCurrentLocale) {
        return window.kendoLocaleManager.getCurrentLocale();
    }
    if (typeof localStorage !== 'undefined') {
        return localStorage.getItem('kendo-locale') || 'es-MX';
    }
    return 'es-MX';
}

export function isBrasilLocale() {
    return getCurrentCulture() === 'pt-BR';
}

/**
 * Normaliza una cadena numérica pt-BR o en-US/es-MX a formato parseable por parseFloat.
 * Acepta entradas mixtas (p. ej. 101,017,500.000 o 101.017.500,000).
 */
function parseBrazilLocaleNumberString(s) {
    const lastComma = s.lastIndexOf(',');
    const lastDot = s.lastIndexOf('.');

    if (lastComma > -1 && lastDot > -1) {
        if (lastComma > lastDot) {
            return s.replace(/\./g, '').replace(',', '.');
        }
        return s.replace(/,/g, '');
    }

    if (lastComma > -1) {
        const commaCount = (s.match(/,/g) || []).length;
        if (commaCount > 1) {
            return s.replace(/,/g, '');
        }

        const digitsAfterComma = s.length - lastComma - 1;
        if (digitsAfterComma === 3) {
            return s.replace(/,/g, '');
        }

        return s.replace(',', '.');
    }

    if (lastDot > -1) {
        const dotCount = (s.match(/\./g) || []).length;
        if (dotCount > 1) {
            return s.replace(/\./g, '');
        }

        const intPart = s.slice(0, lastDot);
        const fracPart = s.slice(lastDot + 1);
        const isEnUsDecimal = fracPart.length <= 2
            || fracPart.length > 3
            || intPart.length >= 5;

        if (isEnUsDecimal) {
            return s;
        }

        return intPart + fracPart;
    }

    return s;
}

/**
 * Convierte un valor de entrada (string o número) a float según la cultura activa.
 */
export function parseLocaleNumber(value) {
    if (value == null || value === '') {
        return NaN;
    }

    if (typeof value === 'number') {
        return value;
    }

    let s = String(value).trim();
    if (!s) {
        return NaN;
    }

    if (isBrasilLocale()) {
        return parseFloat(parseBrazilLocaleNumberString(s));
    }

    return parseFloat(s.replace(/[^0-9.-]/g, ''));
}

/**
 * Formatea un número con separadores según la cultura activa.
 * @param {number|string} value
 * @param {number} decimalPlaces
 */
export function formatLocaleNumber(value, decimalPlaces = 2) {
    const numFloat = parseLocaleNumber(value);

    if (isNaN(numFloat)) {
        return '';
    }

    if (numFloat === 0) {
        return '0';
    }

    if (isBrasilLocale()) {
        return numFloat.toLocaleString('pt-BR', {
            minimumFractionDigits: decimalPlaces,
            maximumFractionDigits: decimalPlaces
        });
    }

    let numString = '' + numFloat.toFixed(decimalPlaces);
    const numParts = numString.split('.');
    const regexp = /(\d+)(\d{3})/;

    while (regexp.test(numParts[0])) {
        numParts[0] = numParts[0].replace(regexp, '$1' + ',' + '$2');
    }

    return numParts.join('.');
}
